/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PACZKA SKÓRKI — `motyw.json` + pliki grafik, sprawdzone ZANIM cokolwiek zostanie opublikowane
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ DWIE WARSTWY, ŻADNA WŁASNA REGUŁA STRUKTURY ──────────────────────────────────────────────┐
 * │ 1. KONTRAKT — wyłącznie `walidujMotyw` z kopii silnika R5-10 (`./sezon/`). Wersja 1 jest    │
 * │    fail-closed: jedno odrzucenie wyłącza cały motyw. Panel NIE ma własnego schematu, więc   │
 * │    nie może się z aplikacją rozjechać.                                                      │
 * │ 2. PLIKI — to, czego aplikacja z samego JSON-a nie sprawdzi: czy grafika z `assets[]`       │
 * │    w ogóle jest w paczce, czy nie przekracza limitu silnika i czy to naprawdę grafika.      │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Werdykt panelu jest SUROWSZY niż zachowanie aplikacji w jednym punkcie: brakujący plik
 * w paczce aplikacja zobaczy dopiero jako pusty obrazek na telefonie ucznia. Tutaj blokuje.
 */
import { walidujMotyw, MAX_BAJTOW_ZASOBU } from './sezon/walidator';
import type { MotywSezonowy, RaportWalidacji } from './sezon/kontrakt';

export const NAZWA_PLIKU_MOTYWU = 'motyw.json';
/**
 * Obecny format paczek R5-10 (narzędzie testu skórek 4.0.10): `theme.json`, często w kopercie odpowiedzi
 * serwera `{ isSuccess, object: {…} }`. Oba pliki przyjmowane (OWNER 11.10: „obsłuż obecny format paczki").
 */
export const NAZWY_PLIKU_MOTYWU = [NAZWA_PLIKU_MOTYWU, 'theme.json'] as const;

/** Plik motywu z paczki: `motyw.json` albo `theme.json` (bez rozróżniania wielkości liter). */
export function plikMotywuZPaczki(pliki: readonly File[]): File | undefined {
  for (const nazwa of NAZWY_PLIKU_MOTYWU) {
    const p = pliki.find((x) => x.name.toLowerCase() === nazwa);
    if (p) return p;
  }
  return undefined;
}

/**
 * Motyw bez koperty — ta sama reguła co walidator aplikacji (`object`, potem `theme`). Do systemu głównego idzie
 * SAM motyw: serwer dopisuje do niego pola runtime (themeId, rewizja, okno) — w kopercie trafiłyby obok motywu.
 */
export function rozpakujMotyw(surowe: unknown): unknown {
  let z = surowe;
  for (const klucz of ['object', 'theme']) {
    if (z && typeof z === 'object' && !Array.isArray(z) && klucz in z) z = (z as Record<string, unknown>)[klucz];
  }
  return z;
}

export type StanZasobu = 'OK' | 'BRAK_PLIKU' | 'ZA_DUZY' | 'NIE_GRAFIKA' | 'NIEJEDNOZNACZNY';

export interface ZasobPaczki {
  id: string;
  url: string;
  rodzaj: string;
  /** Nazwa pliku, którego paczka musi dostarczyć — ostatni człon adresu. */
  nazwaPliku: string;
  plik: File | null;
  stan: StanZasobu;
}

export interface WynikPaczki {
  werdykt: 'VALID' | 'INVALID';
  /** Motyw po walidacji kontraktu. `null` = silnik odrzucił paczkę w całości. */
  motyw: MotywSezonowy | null;
  raport: RaportWalidacji | null;
  zasoby: ZasobPaczki[];
  /** Blokują aktywację. */
  bledy: string[];
  /** Nie blokują — aplikacja pokaże motyw, ale inaczej, niż napisano w paczce. */
  ostrzezenia: string[];
}

function nazwaZUrl(url: string): string {
  const bezZapytania = url.split(/[?#]/)[0] ?? '';
  return decodeURIComponent(bezZapytania.slice(bezZapytania.lastIndexOf('/') + 1));
}

function zasobyZSurowego(surowe: unknown): { id: string; url: string; rodzaj: string }[] {
  if (!surowe || typeof surowe !== 'object') return [];
  const lista = (surowe as { assets?: unknown }).assets;
  if (!Array.isArray(lista)) return [];
  return lista.flatMap((a) => {
    if (!a || typeof a !== 'object') return [];
    const { id, url, type } = a as { id?: unknown; url?: unknown; type?: unknown };
    return typeof id === 'string' && typeof url === 'string'
      ? [{ id, url, rodzaj: typeof type === 'string' ? type : '?' }]
      : [];
  });
}

/**
 * Najniższy silnik, który narysuje paczkę: `compatibility.minEngineVersion` (albo to samo pole na
 * najwyższym poziomie), a schemat 3 (graf sceny) zawsze wymaga silnika 3 — tak mówi kontrakt aplikacji.
 */
export function wymaganySilnik(surowe: unknown): number {
  if (!surowe || typeof surowe !== 'object') return 1;
  const o = surowe as { compatibility?: { minEngineVersion?: unknown }; minEngineVersion?: unknown; schemaVersion?: unknown };
  const zDeklaracji = Number(o.compatibility?.minEngineVersion ?? o.minEngineVersion);
  const zeSchematu = Number(o.schemaVersion) >= 3 ? 3 : 1;
  return Math.max(Number.isInteger(zDeklaracji) && zDeklaracji > 0 ? zDeklaracji : 1, zeSchematu);
}

/** Grafika wektorowa musi być SVG — sprawdzamy treść, nie rozszerzenie nazwy. */
async function wygladaNaSvg(plik: File): Promise<boolean> {
  const poczatek = (await plik.text()).slice(0, 2048);
  return /<svg[\s>]/i.test(poczatek);
}

/**
 * Sprawdza paczkę wskazaną przez operatora (pliki z dysku, nic nie idzie do sieci).
 *
 * @param pliki pliki wybrane w polu „wgraj paczkę" — `motyw.json` i grafiki
 */
export async function sprawdzPaczke(pliki: readonly File[]): Promise<WynikPaczki> {
  const bledy: string[] = [];
  const ostrzezenia: string[] = [];

  const plikMotywu = plikMotywuZPaczki(pliki);
  if (!plikMotywu) {
    return {
      werdykt: 'INVALID', motyw: null, raport: null, zasoby: [], ostrzezenia,
      bledy: [`Paczka nie zawiera pliku ${NAZWA_PLIKU_MOTYWU} (ani theme.json).`],
    };
  }

  let surowe: unknown;
  try {
    surowe = rozpakujMotyw(JSON.parse(await plikMotywu.text()));
  } catch {
    return {
      werdykt: 'INVALID', motyw: null, raport: null, zasoby: [], ostrzezenia,
      bledy: [`${plikMotywu.name} nie jest poprawnym JSON-em.`],
    };
  }

  // ── 1. KONTRAKT — wyłącznie silnik R5-10 ──────────────────────────────────────────────────
  const { motyw, raport } = walidujMotyw(surowe);
  if (!motyw) {
    bledy.push(...(raport.powody.length ? raport.powody : ['silnik odrzucił motyw bez podania powodu']));
  }
  if (raport.przycieteParametry > 0) {
    ostrzezenia.push(`Silnik przytnie ${raport.przycieteParametry} parametr(y) animacji do dozwolonego zakresu.`);
  }
  // Zgodność z wersjami aplikacji: silnik 1 (4.0.0–4.0.9) odrzuca paczki wymagające nowszego silnika
  // i pokazuje zwykły wygląd. To zachowanie silnika (fail-closed), nie błąd paczki — operator ma o nim wiedzieć.
  const wymagany = wymaganySilnik(surowe);
  if (motyw && wymagany > 1) {
    ostrzezenia.push(
      `Skórka wymaga silnika ${wymagany} — zobaczą ją aplikacje 4.0.10 i nowsze. `
      + 'Wersje 4.0.0–4.0.9 (silnik 1) pokażą zwykły wygląd.',
    );
  }

  // ── 2. PLIKI ─────────────────────────────────────────────────────────────────────────────
  const zasoby: ZasobPaczki[] = [];
  for (const a of zasobyZSurowego(surowe)) {
    const nazwaPliku = nazwaZUrl(a.url);
    const pasujace = pliki.filter((p) => p.name === nazwaPliku);
    let stan: StanZasobu = 'OK';
    let plik: File | null = pasujace[0] ?? null;

    if (pasujace.length === 0) {
      stan = 'BRAK_PLIKU';
      bledy.push(`Brak pliku „${nazwaPliku}" dla zasobu „${a.id}".`);
    } else if (pasujace.length > 1) {
      stan = 'NIEJEDNOZNACZNY';
      plik = null;
      bledy.push(`Plik „${nazwaPliku}" występuje w paczce więcej niż raz — nie wiadomo, który to zasób „${a.id}".`);
    } else if (plik && plik.size > MAX_BAJTOW_ZASOBU) {
      stan = 'ZA_DUZY';
      bledy.push(`„${nazwaPliku}" ma ${plik.size} B — limit silnika to ${MAX_BAJTOW_ZASOBU} B.`);
    } else if (plik && a.rodzaj === 'LIGHTWEIGHT_VECTOR_ANIMATION' && !(await wygladaNaSvg(plik))) {
      stan = 'NIE_GRAFIKA';
      bledy.push(`„${nazwaPliku}" ma być grafiką SVG, a nie jest.`);
    }
    zasoby.push({ ...a, nazwaPliku, plik, stan });
  }

  return { werdykt: bledy.length === 0 ? 'VALID' : 'INVALID', motyw, raport, zasoby, bledy, ostrzezenia };
}

/*
 * ┌─ GRAFIKI NA SERWER, ZANIM PACZKA PÓJDZIE DO SYSTEMU GŁÓWNEGO (OWNER 11.10) ──────────────────┐
 * │ Aplikacja wczytuje grafiki WYŁĄCZNIE z HTTPS *.tuszama.pl. Adres względny („tlo.png") znaczy │
 * │ dla niej plik wbudowany w aplikację — grafika z paczki operatora tam nie leży, więc skórka   │
 * │ gasła na telefonie bez słowa. Każdy adres zasobu, który nie jest już adresem HTTPS, musi     │
 * │ wskazywać plik z paczki: ten plik idzie na serwer, a w paczce zostaje adres serwera.         │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 */

/** Pola adresu zasobu — te same, które sprawdza system główny (`Skorki.POLA_ADRESU_ZASOBU`). */
export const POLA_ADRESU_ZASOBU = ['url', 'src', 'staticUrl', 'urlStatyczny'] as const;

/** Treść pliku w base64 (bez prefiksu `data:`), w porcjach — `btoa` na całym pliku przepełnia stos. */
export async function base64Pliku(plik: Blob): Promise<string> {
  const bajty = new Uint8Array(await plik.arrayBuffer());
  let tekst = '';
  for (let i = 0; i < bajty.length; i += 0x8000) {
    tekst += String.fromCharCode(...bajty.subarray(i, i + 0x8000));
  }
  return btoa(tekst);
}

/**
 * Kopia `motyw.json` z adresami grafik z serwera. Oryginał zostaje nietknięty (po błędzie operator
 * poprawia i próbuje ponownie). Ten sam plik użyty w kilku zasobach idzie na serwer raz.
 *
 * `url` bez pliku w paczce to błąd (aplikacja pokazałaby pusty obrazek). Pozostałe pola (np. `staticUrl`)
 * bez pliku zostają bez zmian — mogą wskazywać zasób wbudowany w aplikację.
 *
 * @param wgraj wysyła plik i zwraca adres HTTPS z serwera
 */
export async function wgrajGrafikiPaczki(
  surowy: Record<string, unknown>,
  pliki: readonly File[],
  wgraj: (plik: File) => Promise<string>,
): Promise<{ paczka: Record<string, unknown>; wgrane: string[] }> {
  const paczka = JSON.parse(JSON.stringify(surowy)) as Record<string, unknown>;
  const lista = Array.isArray(paczka.assets) ? (paczka.assets as unknown[]) : [];
  const adresy = new Map<string, string>();
  const wgrane: string[] = [];

  for (const a of lista) {
    if (!a || typeof a !== 'object') continue;
    const zasob = a as Record<string, unknown>;
    for (const pole of POLA_ADRESU_ZASOBU) {
      const wartosc = zasob[pole];
      if (typeof wartosc !== 'string' || /^https:\/\//i.test(wartosc.trim())) continue;
      const nazwaPliku = nazwaZUrl(wartosc);
      const pasujace = pliki.filter((p) => p.name === nazwaPliku);
      if (pasujace.length !== 1) {
        if (pole !== 'url') continue;
        throw new Error(pasujace.length === 0
          ? `Brak pliku „${nazwaPliku}" dla zasobu „${String(zasob.id ?? '?')}".`
          : `Plik „${nazwaPliku}" występuje w paczce więcej niż raz.`);
      }
      let adres = adresy.get(nazwaPliku);
      if (!adres) {
        adres = await wgraj(pasujace[0]!);
        adresy.set(nazwaPliku, adres);
        wgrane.push(nazwaPliku);
      }
      zasob[pole] = adres;
    }
  }
  return { paczka, wgrane };
}
